import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { ArrowRight, MapPin, Search, Star, TreePalm } from "lucide-react";

/** Natural Retreat — partner properties we showcase; booking happens on their own site. */
export default function Retreats() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/retreats").then(({ data }) => setRows(data)).catch(() => {}); }, []);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => `${r.name} ${r.city}`.toLowerCase().includes(needle));
  }, [rows, q]);

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-12 py-14" data-testid="retreats-page">
      <div className="max-w-2xl">
        <div className="overline flex items-center gap-2"><TreePalm size={14} /> Natural Retreat</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05]">
          Stay somewhere<br /><span className="text-secondary italic">the air is still.</span>
        </h1>
        <p className="text-muted-foreground mt-5">
          Partner resorts we know and trust — lakeside tree houses, gardens and slow food, a short
          drive from the city. Rooms are booked directly with each property.
        </p>
      </div>

      <div className="relative mt-10 max-w-md">
        <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by property or city"
          className="pl-11 h-12 bg-background rounded-full"
          data-testid="retreat-search"
        />
      </div>

      {shown.length === 0 ? (
        <p className="text-muted-foreground mt-12" data-testid="retreat-empty">
          {rows.length === 0 ? "Partner properties are being added — do check back." : "No properties found."}
        </p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {shown.map((r) => (
            <Link
              to={`/retreats/${r.id}`}
              key={r.id}
              className="group rounded-[1.75rem] border border-border bg-background overflow-hidden hover:border-primary/40 transition-colors"
              data-testid={`retreat-card-${r.id}`}
            >
              <div className="aspect-[4/3] overflow-hidden bg-muted">
                {r.images?.[0] ? (
                  <img
                    src={mediaUrl(r.images[0])}
                    alt={r.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.05]"
                  />
                ) : (
                  // Partner photos are added by the admin once the partnership is signed —
                  // never borrowed from someone else's website.
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 text-muted-foreground bg-secondary/5" data-testid={`retreat-photos-pending-${r.id}`}>
                    <TreePalm size={22} className="text-secondary/60" />
                    <span className="text-xs">Photos coming soon</span>
                  </div>
                )}
              </div>
              <div className="p-6">
                <h2 className="font-display text-2xl leading-tight">{r.name}</h2>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2">
                  <MapPin size={13} /> {r.city}
                </div>
                {r.rating_text ? (
                  <div className="flex items-center gap-1.5 text-sm text-secondary mt-2">
                    <Star size={13} fill="currentColor" /> {r.rating_text}
                  </div>
                ) : null}
                <p className="text-sm text-muted-foreground mt-4 line-clamp-3">{r.about}</p>
                <span className="inline-flex items-center gap-1.5 text-sm text-primary mt-5 group-hover:gap-3 transition-all">
                  View property <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </main>
  );
}
